function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function integerValue(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
}

function canonicalIconStem(stem) {
  const normalized = trimString(stem);
  const icMatch = normalized.match(/^ic_(\d+)$/i);
  if (icMatch) {
    return `IC_${icMatch[1]}`;
  }
  return normalized;
}

export function resolveCdnAssetUrl(path) {
  const normalized = trimString(path);
  if (!normalized) {
    return "";
  }
  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }
  const resolver = globalThis.window?.__fishystuffResolveCdnUrl;
  if (typeof resolver === "function") {
    return trimString(resolver(normalized));
  }
  return normalized;
}

export function normalizeItemIconPath(value) {
  const raw = trimString(value);
  if (!raw) {
    return "";
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const withoutQuery = raw.split(/[?#]/, 1)[0].replaceAll("\\", "/").trim();
  if (!withoutQuery) {
    return "";
  }
  const basename = withoutQuery.split("/").pop() || "";
  const extensionMatch = basename.match(/\.([a-z0-9]+)$/i);
  const stem = canonicalIconStem(extensionMatch ? basename.slice(0, -extensionMatch[0].length) : basename);
  if (!stem) {
    return "";
  }

  const hasIconPathPrefix = /^\/?images\/items\//i.test(withoutQuery);
  const hasKnownIconExtension = extensionMatch && /^(?:dds|png|webp|jpg|jpeg|gif|avif)$/i.test(extensionMatch[1]);
  const hasSourceIconPath = /(?:^|\/)(?:ui_texture|new_icon|quest|fishicons|icon)(?:\/|$)/i.test(withoutQuery);
  const looksLikeKnownIconStem = /^(?:IC_\d+|\d{5,8}(?:_\d+)?|[A-Za-z][A-Za-z0-9_]*\d[A-Za-z0-9_]*)$/.test(stem);
  if (!hasIconPathPrefix && !hasKnownIconExtension && !hasSourceIconPath && !looksLikeKnownIconStem) {
    return "";
  }
  return `/images/items/${stem}.webp`;
}

function catalogItemForId(catalog, itemId) {
  const parsed = integerValue(itemId);
  if (parsed == null || parsed <= 0) {
    return null;
  }
  if (catalog instanceof Map) {
    return catalog.get(parsed) || catalog.get(String(parsed)) || null;
  }
  if (Array.isArray(catalog)) {
    return catalog.find((item) => integerValue(item?.itemId ?? item?.item_id ?? item?.id) === parsed) || null;
  }
  return null;
}

export function itemIconLookup(catalogItems) {
  const lookup = new Map();
  for (const item of Array.isArray(catalogItems) ? catalogItems : []) {
    const itemId = integerValue(item?.itemId ?? item?.item_id ?? item?.id);
    if (itemId != null && itemId > 0 && !lookup.has(itemId)) {
      lookup.set(itemId, item);
    }
  }
  return lookup;
}

export function resolveFishItemIconUrl(itemId, options = {}) {
  const parsedItemId = integerValue(itemId);
  const catalogItem = catalogItemForId(options.itemLookup, parsedItemId)
    || catalogItemForId(options.catalogItems, parsedItemId);
  const directIconPath = normalizeItemIconPath(options.iconImage)
    || normalizeItemIconPath(catalogItem?.iconImage ?? catalogItem?.icon_image);
  if (directIconPath) {
    return resolveCdnAssetUrl(directIconPath);
  }

  const iconItemId = integerValue(options.iconItemId ?? options.icon_item_id)
    || integerValue(catalogItem?.iconItemId ?? catalogItem?.icon_item_id);
  if (iconItemId == null || iconItemId <= 0) {
    return "";
  }

  const resolver = globalThis.window?.__fishystuffResolveFishItemIconUrl;
  if (typeof resolver === "function") {
    return trimString(resolver(iconItemId));
  }
  return `/images/items/${String(iconItemId).padStart(8, "0")}.webp`;
}
